import { useEffect, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { authApi } from '@/lib/pillarApi';
import { cn, runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    MfaPolicyStatusResponse,
    MfaPolicyStatusResponseSchema,
    SessionUserProfileDetailedResponse,
    SetupTotpResponse,
    SetupTotpResponseSchema,
} from '@anchorbase/pillar-sdk';

interface MfaControlsCardProps {
    readonly profile: Pick<SessionUserProfileDetailedResponse, 'totp_enabled'> | null;
}

interface MfaSliderProps {
    readonly checked: boolean;
    readonly disabled?: boolean;
    readonly label: string;
    readonly destructive?: boolean;
    readonly title?: string;
    readonly onToggle: () => void;
}

function MfaSlider({
    checked,
    disabled = false,
    label,
    destructive = false,
    title,
    onToggle,
}: MfaSliderProps): ReactElement {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            title={title}
            onClick={onToggle}
            className={cn(
                'inline-flex items-center gap-2 rounded-md px-1 py-1 text-sm font-medium disabled:opacity-50',
                destructive ? 'text-destructive' : 'text-foreground'
            )}
        >
            <span>{checked ? 'Enabled' : 'Disabled'}</span>
            <span
                aria-hidden="true"
                className={cn(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    checked ? (destructive ? 'bg-destructive' : 'bg-primary') : 'bg-muted'
                )}
            >
                <span
                    className={cn(
                        'inline-block h-5 w-5 rounded-full bg-background shadow transition-transform',
                        checked ? 'translate-x-5' : 'translate-x-1'
                    )}
                />
            </span>
        </button>
    );
}

interface MfaMethodCardProps {
    readonly title: string;
    readonly description: string;
    readonly checked: boolean;
    readonly disabled?: boolean;
    readonly label: string;
    readonly onToggle: () => void;
    readonly children?: ReactNode;
}

function MfaMethodCard({
    title,
    description,
    checked,
    disabled = false,
    label,
    onToggle,
    children,
}: MfaMethodCardProps): ReactElement {
    return (
        <div className="space-y-4 rounded-lg border p-4">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-sm text-muted-foreground">{description}</p>
                </div>
                <MfaSlider
                    checked={checked}
                    disabled={disabled}
                    label={label}
                    onToggle={onToggle}
                />
            </div>
            {children}
        </div>
    );
}

export function MfaControlsCard({ profile }: MfaControlsCardProps) {
    const log = createComponentLogger('MfaControlsCard');
    const queryClient = useQueryClient();
    const [totpCode, setTotpCode] = useState('');
    const [showTotpSetup, setShowTotpSetup] = useState(false);
    const [optimisticTotpEnabled, setOptimisticTotpEnabled] = useState(false);
    const [emailMfaCode, setEmailMfaCode] = useState('');
    const [showEmailMfaVerification, setShowEmailMfaVerification] = useState(false);
    const [optimisticEmailEnabled, setOptimisticEmailEnabled] = useState(false);
    const [smsMfaCode, setSmsMfaCode] = useState('');
    const [showSmsMfaVerification, setShowSmsMfaVerification] = useState(false);
    const [optimisticSmsEnabled, setOptimisticSmsEnabled] = useState(false);
    const [showDisableMfaConfirm, setShowDisableMfaConfirm] = useState(false);
    const [showMethods, setShowMethods] = useState(false);

    const { data: mfaStatus } = useQuery<MfaPolicyStatusResponse>({
        queryKey: ['mfa-status'],
        queryFn: async () => {
            const response = await authApi.getMfaStatus();
            return MfaPolicyStatusResponseSchema.parse(response);
        },
        enabled: profile !== null,
        staleTime: 0,
        refetchOnMount: 'always',
    });

    const { data: totpSetup } = useQuery<SetupTotpResponse>({
        queryKey: ['totp-setup'],
        queryFn: async () => {
            const response = await authApi.setupTOTP();
            return SetupTotpResponseSchema.parse(response);
        },
        enabled: showTotpSetup && !profile?.totp_enabled,
    });

    const invalidateMfaQueries = (): void => {
        runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
            log.error('Error invalidating profile query', err);
        });
        runInBackground(queryClient.invalidateQueries({ queryKey: ['mfa-status'] }), (err) => {
            log.error('Error invalidating MFA status query', err);
        });
    };

    const enableTotpMutation = useMutation({
        mutationFn: authApi.enableTOTP,
        onSuccess: () => {
            invalidateMfaQueries();
            setShowTotpSetup(false);
            setOptimisticTotpEnabled(false);
            setTotpCode('');
        },
        onError: () => {
            setOptimisticTotpEnabled(false);
        },
    });

    const disableTotpMutation = useMutation({
        mutationFn: authApi.disableTOTP,
        onSuccess: () => {
            if (enabledMethodCount === 1) {
                setShowMethods(false);
            }
            invalidateMfaQueries();
        },
    });

    const disableMfaMutation = useMutation({
        mutationFn: authApi.disableMfa,
        onSuccess: () => {
            invalidateMfaQueries();
            setShowDisableMfaConfirm(false);
            setShowTotpSetup(false);
            setShowEmailMfaVerification(false);
            setShowSmsMfaVerification(false);
            setShowMethods(false);
            setOptimisticTotpEnabled(false);
            setOptimisticEmailEnabled(false);
            setOptimisticSmsEnabled(false);
            setTotpCode('');
            setEmailMfaCode('');
            setSmsMfaCode('');
        },
    });

    const requestEmailMfaMutation = useMutation({
        mutationFn: authApi.requestEmailMfaActivation,
        onSuccess: () => {
            setShowEmailMfaVerification(true);
            setEmailMfaCode('');
        },
        onError: () => {
            setShowEmailMfaVerification(false);
            setOptimisticEmailEnabled(false);
        },
    });

    const verifyEmailMfaMutation = useMutation({
        mutationFn: authApi.verifyEmailMfaActivation,
        onSuccess: () => {
            invalidateMfaQueries();
            setShowEmailMfaVerification(false);
            setOptimisticEmailEnabled(false);
            setEmailMfaCode('');
        },
        onError: () => {
            setOptimisticEmailEnabled(false);
        },
    });

    const disableEmailMfaMutation = useMutation({
        mutationFn: authApi.disableEmailMfa,
        onSuccess: () => {
            if (enabledMethodCount === 1) {
                setShowMethods(false);
            }
            invalidateMfaQueries();
        },
    });

    const requestSmsMfaMutation = useMutation({
        mutationFn: authApi.requestSmsMfaActivation,
        onSuccess: () => {
            setShowSmsMfaVerification(true);
            setSmsMfaCode('');
        },
        onError: () => {
            setShowSmsMfaVerification(false);
            setOptimisticSmsEnabled(false);
        },
    });

    const verifySmsMfaMutation = useMutation({
        mutationFn: authApi.verifySmsMfaActivation,
        onSuccess: () => {
            invalidateMfaQueries();
            setShowSmsMfaVerification(false);
            setOptimisticSmsEnabled(false);
            setSmsMfaCode('');
        },
        onError: () => {
            setOptimisticSmsEnabled(false);
        },
    });

    const disableSmsMfaMutation = useMutation({
        mutationFn: authApi.disableSmsMfa,
        onSuccess: () => {
            if (enabledMethodCount === 1) {
                setShowMethods(false);
            }
            invalidateMfaQueries();
        },
    });

    const handleEnableTOTP = (event: FormEvent): void => {
        event.preventDefault();
        if (totpCode && totpSetup) {
            enableTotpMutation.mutate({
                secret: totpSetup.secret,
                code: totpCode,
            });
        }
    };

    const handleVerifyEmailMfa = (event: FormEvent): void => {
        event.preventDefault();
        verifyEmailMfaMutation.mutate({ code: emailMfaCode });
    };

    const handleVerifySmsMfa = (event: FormEvent): void => {
        event.preventDefault();
        verifySmsMfaMutation.mutate({ code: smsMfaCode });
    };

    const policyEnabled: boolean = mfaStatus?.required === true;
    const totpEnabled: boolean =
        optimisticTotpEnabled || mfaStatus?.totp_enabled === true || profile?.totp_enabled === true;
    const emailEnabled: boolean = optimisticEmailEnabled || mfaStatus?.email_allowed === true;
    const smsEnabled: boolean = optimisticSmsEnabled || mfaStatus?.sms_allowed === true;
    const enabledMethodCount: number =
        (mfaStatus?.totp_enabled === true || profile?.totp_enabled === true ? 1 : 0) +
        (mfaStatus?.email_allowed === true ? 1 : 0) +
        (mfaStatus?.sms_allowed === true ? 1 : 0);
    const disableMfaBlockedByOrganizationPolicy: boolean =
        policyEnabled && mfaStatus?.can_disable === false;
    const disableMfaBlockedReason =
        'MFA cannot be disabled because at least one organization requires MFA for its users.';

    useEffect(() => {
        if (policyEnabled) {
            setShowMethods(true);
        }
    }, [policyEnabled]);

    const cancelTotpSetup = (): void => {
        setShowTotpSetup(false);
        setOptimisticTotpEnabled(false);
        setTotpCode('');
    };

    const cancelEmailMfaVerification = (): void => {
        setShowEmailMfaVerification(false);
        setOptimisticEmailEnabled(false);
        setEmailMfaCode('');
    };

    const cancelSmsMfaVerification = (): void => {
        setShowSmsMfaVerification(false);
        setOptimisticSmsEnabled(false);
        setSmsMfaCode('');
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                    <CardTitle>Multifactor Authentication (MFA)</CardTitle>
                    <CardDescription>
                        Add an extra layer of security to your account
                    </CardDescription>
                </div>
                <MfaSlider
                    checked={policyEnabled}
                    destructive={policyEnabled}
                    disabled={
                        disableMfaMutation.isPending ||
                        disableMfaBlockedByOrganizationPolicy
                    }
                    label={policyEnabled ? 'Disable MFA' : 'Enable MFA'}
                    title={
                        disableMfaBlockedByOrganizationPolicy
                            ? disableMfaBlockedReason
                            : undefined
                    }
                    onToggle={() => {
                        if (policyEnabled) {
                            setShowDisableMfaConfirm(true);
                            return;
                        }
                        setShowMethods(true);
                    }}
                />
            </CardHeader>
            <CardContent className="space-y-4">
                {disableMfaMutation.error ? (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {disableMfaMutation.error instanceof Error
                                ? disableMfaMutation.error.message
                                : 'Failed to disable MFA'}
                        </AlertDescription>
                    </Alert>
                ) : null}
                {disableMfaBlockedByOrganizationPolicy ? (
                    <Alert>
                        <AlertDescription>{disableMfaBlockedReason}</AlertDescription>
                    </Alert>
                ) : null}

                {showMethods ? (
                    <>
                        <MfaMethodCard
                            title="Authenticator app"
                            description="Use an authenticator app to generate codes."
                            checked={totpEnabled}
                            disabled={
                                enableTotpMutation.isPending ||
                                disableTotpMutation.isPending
                            }
                            label={
                                totpEnabled
                                    ? 'Disable authenticator app MFA'
                                    : 'Enable authenticator app MFA'
                            }
                            onToggle={() => {
                                if (mfaStatus?.totp_enabled === true || profile?.totp_enabled) {
                                    disableTotpMutation.mutate();
                                    return;
                                }
                                setOptimisticTotpEnabled(true);
                                setShowTotpSetup(true);
                            }}
                        >
                            {disableTotpMutation.error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {disableTotpMutation.error instanceof Error
                                            ? disableTotpMutation.error.message
                                            : 'Failed to disable authenticator app MFA'}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                        </MfaMethodCard>

                        <MfaMethodCard
                            title="Email"
                            description="Receive MFA codes at your primary email address."
                            checked={emailEnabled}
                            disabled={
                                requestEmailMfaMutation.isPending ||
                                verifyEmailMfaMutation.isPending ||
                                disableEmailMfaMutation.isPending ||
                                mfaStatus?.primary_email_available === false
                            }
                            label={emailEnabled ? 'Disable email MFA' : 'Enable email MFA'}
                            onToggle={() => {
                                if (mfaStatus?.email_allowed === true) {
                                    disableEmailMfaMutation.mutate();
                                    return;
                                }
                                setOptimisticEmailEnabled(true);
                                setShowEmailMfaVerification(true);
                                requestEmailMfaMutation.mutate();
                            }}
                        >
                            {mfaStatus?.primary_email_available === false ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        Add a primary email address before enabling email MFA.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {requestEmailMfaMutation.error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {requestEmailMfaMutation.error instanceof Error
                                            ? requestEmailMfaMutation.error.message
                                            : 'Failed to send email MFA code'}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {disableEmailMfaMutation.error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {disableEmailMfaMutation.error instanceof Error
                                            ? disableEmailMfaMutation.error.message
                                            : 'Failed to disable email MFA'}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                        </MfaMethodCard>

                        <MfaMethodCard
                            title="Text message"
                            description="Receive MFA codes at your primary phone number."
                            checked={smsEnabled}
                            disabled={
                                requestSmsMfaMutation.isPending ||
                                verifySmsMfaMutation.isPending ||
                                disableSmsMfaMutation.isPending ||
                                mfaStatus?.primary_phone_available === false
                            }
                            label={smsEnabled ? 'Disable text message MFA' : 'Enable text message MFA'}
                            onToggle={() => {
                                if (mfaStatus?.sms_allowed === true) {
                                    disableSmsMfaMutation.mutate();
                                    return;
                                }
                                setOptimisticSmsEnabled(true);
                                setShowSmsMfaVerification(true);
                                requestSmsMfaMutation.mutate();
                            }}
                        >
                            {mfaStatus?.primary_phone_available === false ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        Add a primary phone number before enabling SMS MFA.
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {requestSmsMfaMutation.error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {requestSmsMfaMutation.error instanceof Error
                                            ? requestSmsMfaMutation.error.message
                                            : 'Failed to send SMS MFA code'}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {disableSmsMfaMutation.error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {disableSmsMfaMutation.error instanceof Error
                                            ? disableSmsMfaMutation.error.message
                                            : 'Failed to disable SMS MFA'}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                        </MfaMethodCard>
                    </>
                ) : null}
            </CardContent>

            <ConfirmationModal
                open={showDisableMfaConfirm}
                onOpenChange={setShowDisableMfaConfirm}
                title="Disable MFA"
                message="Disable MFA for your account?"
                onConfirm={() => disableMfaMutation.mutate()}
                confirmLabel="Disable MFA"
                isLoading={disableMfaMutation.isPending}
            >
                <p className="text-sm text-muted-foreground">
                    Disabling MFA reduces protection against unauthorized access and
                    other cyber risks.
                </p>
            </ConfirmationModal>

            <Dialog open={showTotpSetup} onOpenChange={(open) => !open && cancelTotpSetup()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Enable authenticator app MFA</DialogTitle>
                        <DialogDescription>
                            Scan the QR code or enter the secret in your authenticator app.
                        </DialogDescription>
                    </DialogHeader>
                    {totpSetup ? (
                        <form onSubmit={handleEnableTOTP} className="space-y-4">
                            <div className="flex justify-center rounded-lg border bg-white p-4">
                                <img src={totpSetup.qr_code} alt="TOTP QR Code" />
                            </div>
                            <div className="space-y-2">
                                <Label>Manual secret</Label>
                                <Input
                                    value={totpSetup.secret}
                                    readOnly
                                    className="font-mono text-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="totpCode">Verification Code</Label>
                                <Input
                                    id="totpCode"
                                    type="text"
                                    value={totpCode}
                                    onChange={(event) => setTotpCode(event.target.value)}
                                    placeholder="Enter 6-digit code"
                                    maxLength={6}
                                    required
                                    disabled={enableTotpMutation.isPending}
                                />
                            </div>
                            {enableTotpMutation.error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {enableTotpMutation.error instanceof Error
                                            ? enableTotpMutation.error.message
                                            : 'Failed to enable authenticator app MFA'}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={cancelTotpSetup}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        enableTotpMutation.isPending || totpCode.length !== 6
                                    }
                                >
                                    {enableTotpMutation.isPending ? 'Verifying...' : 'Enable'}
                                </Button>
                            </DialogFooter>
                        </form>
                    ) : (
                        <p className="text-sm text-muted-foreground">Preparing setup...</p>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={showEmailMfaVerification}
                onOpenChange={(open) => !open && cancelEmailMfaVerification()}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Enable email MFA</DialogTitle>
                        <DialogDescription>
                            Enter the code sent to your primary email address.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleVerifyEmailMfa} className="space-y-4">
                        {requestEmailMfaMutation.data ? (
                            <Alert>
                                <AlertDescription>
                                    Email MFA code sent to{' '}
                                    {requestEmailMfaMutation.data.delivery_destination}.
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        <div className="space-y-2">
                            <Label htmlFor="emailMfaCode">Email MFA Code</Label>
                            <Input
                                id="emailMfaCode"
                                type="text"
                                value={emailMfaCode}
                                onChange={(event) => setEmailMfaCode(event.target.value)}
                                placeholder="Enter code"
                                required
                                disabled={verifyEmailMfaMutation.isPending}
                            />
                        </div>
                        {verifyEmailMfaMutation.error ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {verifyEmailMfaMutation.error instanceof Error
                                        ? verifyEmailMfaMutation.error.message
                                        : 'Failed to enable email MFA'}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={cancelEmailMfaVerification}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => requestEmailMfaMutation.mutate()}
                                disabled={requestEmailMfaMutation.isPending}
                            >
                                {requestEmailMfaMutation.isPending ? 'Sending...' : 'Resend'}
                            </Button>
                            <Button
                                type="submit"
                                disabled={
                                    verifyEmailMfaMutation.isPending ||
                                    emailMfaCode.trim().length === 0
                                }
                            >
                                {verifyEmailMfaMutation.isPending ? 'Verifying...' : 'Verify'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showSmsMfaVerification}
                onOpenChange={(open) => !open && cancelSmsMfaVerification()}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Enable text message MFA</DialogTitle>
                        <DialogDescription>
                            Enter the code sent to your primary phone number.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleVerifySmsMfa} className="space-y-4">
                        {requestSmsMfaMutation.data ? (
                            <Alert>
                                <AlertDescription>
                                    SMS MFA code sent to{' '}
                                    {requestSmsMfaMutation.data.delivery_destination}.
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        <div className="space-y-2">
                            <Label htmlFor="smsMfaCode">SMS MFA Code</Label>
                            <Input
                                id="smsMfaCode"
                                type="text"
                                value={smsMfaCode}
                                onChange={(event) => setSmsMfaCode(event.target.value)}
                                placeholder="Enter code"
                                required
                                disabled={verifySmsMfaMutation.isPending}
                            />
                        </div>
                        {verifySmsMfaMutation.error ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {verifySmsMfaMutation.error instanceof Error
                                        ? verifySmsMfaMutation.error.message
                                        : 'Failed to enable SMS MFA'}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={cancelSmsMfaVerification}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => requestSmsMfaMutation.mutate()}
                                disabled={requestSmsMfaMutation.isPending}
                            >
                                {requestSmsMfaMutation.isPending ? 'Sending...' : 'Resend'}
                            </Button>
                            <Button
                                type="submit"
                                disabled={
                                    verifySmsMfaMutation.isPending ||
                                    smsMfaCode.trim().length === 0
                                }
                            >
                                {verifySmsMfaMutation.isPending ? 'Verifying...' : 'Verify'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
